import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  TextField,
  Button,
  Box,
  Card,
  Typography,
  Switch,
  FormControlLabel,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { API_ENDPOINTS } from "../../../config/api";

const API_URL = API_ENDPOINTS.CATEGORIES;

const EditCategoryForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [category, setCategory] = useState({
    name: "",
    publish: false,
  });
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success", // 'success' | 'error'
  });
  const [redirectOnClose, setRedirectOnClose] = useState(false);

  useEffect(() => {
    fetchCategory();
  }, []);

  const fetchCategory = async () => {
    try {
      const response = await axios.get(API_URL.EDIT(id));
      setCategory({
        name: response.data.category.name || "",
        publish: response.data.category.publish || false,
      });
      setLoading(false);
    } catch (error) {
      console.error("Error fetching category:", error);
      setSnackbar({
        open: true,
        message: "Failed to load category data",
        severity: "error",
      });
      setRedirectOnClose(false);
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setCategory({ ...category, [e.target.name]: e.target.value });
  };

  const handlePublishToggle = () => {
    setCategory((prev) => ({ ...prev, publish: !prev.publish }));
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
    if (redirectOnClose) {
      navigate(-1); // Go back
    }
  };

  const handleUpdate = async () => {
    try {
      await axios.put(API_URL.EDIT(id), category);
      setSnackbar({
        open: true,
        message: "Category updated successfully!",
        severity: "success",
      });
      setRedirectOnClose(true);
    } catch (error) {
      let userMessage = "Failed to update category.";
      if (
        error.response?.data?.message &&
        error.response.data.message.includes("foreign key constraint fails")
      ) {
        userMessage =
          "Cannot update this category because it is assigned to other records. Please reassign or remove those records first.";
      } else if (error.response?.data?.message) {
        userMessage = "Failed to update category: " + error.response.data.message;
      }
      setSnackbar({
        open: true,
        message: userMessage,
        severity: "error",
      });
      setRedirectOnClose(false);
    }
  };

  if (loading) {
    return <CircularProgress sx={{ display: "block", margin: "auto", mt: 5 }} />;
  }

  return (
    <>
      <Card sx={{ p: 4, maxWidth: 500, mx: "auto", mt: 5 }}>
        <Typography variant="h5" mb={3}>
          Edit Category
        </Typography>
        <Box display="flex" flexDirection="column" gap={3}>
          <TextField
            label="Category Name"
            name="name"
            value={category.name}
            onChange={handleChange}
            required
          />
          <FormControlLabel
            control={
              <Switch
                checked={category.publish}
                onChange={handlePublishToggle}
                color="primary"
              />
            }
            label="Published"
          />
          <Box display="flex" justifyContent="space-between" gap={2}>
            <Button variant="contained" color="primary" onClick={handleUpdate}>
              Update
            </Button>
            <Button variant="outlined" color="secondary" onClick={() => navigate(-1)}>
              Cancel
            </Button>
          </Box>
        </Box>
      </Card>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={1000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EditCategoryForm;
